import { useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Supported image formats
const SUPPORTED_FORMATS = ['image/avif', 'image/webp', 'image/jpeg', 'image/png', 'image/gif'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function useStoreImageUpload() {
  const [isUploading, setIsUploading] = useState(false);

  const uploadImage = useCallback(async (file: File, imageType: 'storefront' | 'cover'): Promise<string | null> => {
    try {
      setIsUploading(true);
      console.log(`Starting ${imageType} image upload for:`, file.name);

      // Validate file type
      if (!SUPPORTED_FORMATS.includes(file.type)) {
        throw new Error('Unsupported file format. Please upload an AVIF, WebP, JPEG, PNG, or GIF image.');
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        throw new Error('File size exceeds 5MB limit.');
      }

      // Create a unique filename with user ID to help with RLS policies
      const timestamp = Date.now();
      const extension = file.name.split('.').pop();
      // Try to get current user ID, or use 'anonymous' if not available
      const { data: { user } } = await supabase.auth.getUser();
      const userPrefix = user?.id ? `${user.id}` : 'anonymous';
      const fileName = `${userPrefix}/${imageType}-${timestamp}.${extension}`;
      
      // First check if user is authenticated
      if (!user) {
        console.warn("User not authenticated, upload might fail due to RLS policies");
      }
      
      console.log(`Uploading to bucket: store-images, path: ${fileName}`);
      
      // Upload the file
      const { data, error } = await supabase.storage
        .from('store-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });
        
      if (error) {
        console.error('Upload failed:', error);
        
        // Check for common error messages and provide helpful responses
        if (error.message.includes('Permission denied')) {
          throw new Error('Permission denied. Please make sure you are logged in and have the correct permissions.');
        } else if (error.message.includes('entity too large')) {
          throw new Error('File size too large. Please upload a smaller image (under 5MB).');
        } else if (error.message.includes('bucket')) {
          throw new Error('Storage bucket not found. Please contact support.');
        } else {
          throw new Error(`Upload failed: ${error.message}`);
        }
      }
      
      // Get public URL
      const { data: urlData } = supabase.storage
        .from('store-images')
        .getPublicUrl(data.path);
      
      console.log('Upload successful, URL:', urlData.publicUrl);  
      return urlData.publicUrl;
    } catch (error) {
      console.error('Image upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload image';
      toast.error(errorMessage);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const deleteImage = useCallback(async (imageUrl: string | null): Promise<boolean> => {
    if (!imageUrl) return true;
    
    try {
      // Extract the path from the URL
      // Example URL: https://xxxx.supabase.co/storage/v1/object/public/store-images/user123/storefront-12345.jpg
      // We need to extract: user123/storefront-12345.jpg
      
      const urlParts = imageUrl.split('/store-images/');
      if (urlParts.length < 2) {
        console.log('Invalid image URL format:', imageUrl);
        return false;
      }
      
      const filePath = urlParts[1];
      console.log('Deleting file:', filePath);
      
      // Delete operation
      const { error } = await supabase.storage
        .from('store-images')
        .remove([filePath]);
        
      if (error) {
        console.error('Delete failed:', error);
        
        // Check common error causes
        if (error.message.includes('Permission denied')) {
          throw new Error("You don't have permission to delete this image.");
        } else {
          throw new Error(`Delete failed: ${error.message}`);
        }
      }
      
      console.log('File deleted successfully');
      return true;
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete image");
      return false;
    }
  }, []);

  return {
    uploadImage,
    deleteImage,
    isUploading
  };
} 